import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeSavedTemplate,
  WelcomeVideoUploadMeta
} from "@/api/types/outgoingIntake";
import { intakeEstimate } from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";
import {
  startVideoUpload,
  templateVideoKey,
  useVideoUpload,
  type VideoUpload
} from "@/features/requestsIde/editor/welcomeVideo/uploadStore";
import type { TalkingPointsInput } from "@/features/requestsIde/editor/welcomeVideo/welcomeVideo";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";

export type TemplateWelcomeVideo = {
  url: string | null;
  /** The firm library entry it plays. */
  videoId: string | null;
  upload: VideoUpload | null;
  uploadFile: (file: File, meta?: WelcomeVideoUploadMeta) => void;
  /** Use a video already in the firm's library. */
  pickLibraryVideo: (videoId: string) => Promise<void>;
  remove: () => Promise<void>;
  recording: boolean;
  setRecording: (open: boolean) => void;
  library: boolean;
  setLibrary: (open: boolean) => void;
  /** No client name or dates: every intake from the template plays it. */
  talkingPoints: TalkingPointsInput;
  /** Casey's talking points; empty when it couldn't. */
  askCasey: () => Promise<string[]>;
};

/** A template's welcome video: one of the firm's shared videos. */
export const useTemplateWelcomeVideo = (
  template: OutgoingIntakeSavedTemplate | null,
  name: string,
  schema: OutgoingIntakeFormNode[],
  onSaved: (template: OutgoingIntakeSavedTemplate) => void
): TemplateWelcomeVideo => {
  const attorneyName = useSelector(lawyerSelectors.selectFullName);
  const firmName = useSelector(lawyerSelectors.selectPracticeName);
  const [recording, setRecording] = useState(false);
  const [library, setLibrary] = useState(false);
  const templateId = template?.template_id ?? null;
  const upload = useVideoUpload(
    templateId ? templateVideoKey(templateId) : null
  );

  const talkingPoints = useMemo(
    () => ({
      clientName: null,
      attorneyName: attorneyName?.trim() || null,
      firmName: firmName || null,
      title: name,
      estimate: intakeEstimate(schema),
      due: null
    }),
    [attorneyName, firmName, name, schema]
  );

  const uploadFile = (file: File, meta: WelcomeVideoUploadMeta = {}) => {
    if (!templateId) return;
    startVideoUpload({
      key: templateVideoKey(templateId),
      upload: (onProgress, signal) =>
        OutgoingIntakesAPI.uploadSavedTemplateWelcomeVideo(templateId, file, {
          filename: file.name,
          durationSeconds: meta.durationSeconds,
          trim: meta.trim,
          onProgress,
          signal
        }),
      onDone: onSaved,
      doneMessage: "Welcome video saved and shared with the firm"
    });
  };

  const pickLibraryVideo = async (videoId: string) => {
    if (!templateId) return;
    onSaved(
      await OutgoingIntakesAPI.setSavedTemplateLibraryVideo(templateId, videoId)
    );
  };

  const askCasey = async () =>
    templateId
      ? OutgoingIntakesAPI.draftTemplateTalkingPoints(templateId, {
          attorney_name: talkingPoints.attorneyName,
          firm_name: talkingPoints.firmName
        })
      : [];

  const remove = async () => {
    if (!templateId) return;
    try {
      onSaved(
        await OutgoingIntakesAPI.deleteSavedTemplateWelcomeVideo(templateId)
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't remove the video");
    }
  };

  return {
    url: template?.welcome_video_url ?? null,
    videoId: template?.welcome_video_id ?? null,
    upload,
    uploadFile,
    pickLibraryVideo,
    remove,
    recording,
    setRecording,
    library,
    setLibrary,
    talkingPoints,
    askCasey
  };
};
